import { useEffect, useRef, useState } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import './styles/community.scss';
import PageHeader from '../../components/PageHeader';
import MeetingEmbed from '../../components/MeetingEmbed';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import { createChatConnection } from '../../apiHelper/chatHub';

type Attachment = { id?: string; fileUrl: string; fileName: string; contentType: string; size: number };
type Message = {
    id: string; channelId: string; userId: string; authorName: string; authorIsAdmin?: boolean;
    text: string; createdAt: string; editedAt?: string | null; attachments: Attachment[];
};
type Channel = {
    id: string; name: string; description: string; isPublic: boolean;
    allowMessages: boolean; allowImages: boolean; enableMeeting: boolean; meetUrl: string; role: number;
};

function initials(name: string) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
function fmtTime(iso: string) {
    return new Date(iso).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Community() {
    const [channels, setChannels] = useState<Channel[]>([]);
    const [meId, setMeId] = useState<string>("");
    const [activeId, setActiveId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [text, setText] = useState("");
    const [pending, setPending] = useState<Attachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [error, setError] = useState(false);
    const [mobileChat, setMobileChat] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [brokenAtts, setBrokenAtts] = useState<Set<string>>(new Set());
    // Src of an attachment shown enlarged in the in-page lightbox (null = closed).
    const [lightbox, setLightbox] = useState<string | null>(null);
    // Local blob previews keyed by the uploaded fileUrl, so an image the user just picked
    // renders instantly (in the composer and in the message they send) without waiting on
    // the storage server to serve the uploaded copy back.
    const [previews, setPreviews] = useState<Record<string, string>>({});
    const previewUrlsRef = useRef<string[]>([]);
    // fileUrls already fetched (or being fetched) as a blob, so we pull each attachment
    // through the auth-gated endpoint exactly once. Includes just-uploaded images, which
    // already have a local blob preview.
    const fetchedRef = useRef<Set<string>>(new Set());

    const api = new ApiHelper();
    const connRef = useRef<HubConnection | null>(null);
    const activeIdRef = useRef<string | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    // When we prepend a page of older messages, we stash the scroll height captured just
    // before the list grows so the scroll effect can hold the viewport on the message the
    // user was reading instead of snapping back to the newest one.
    const prependRef = useRef<number | null>(null);

    const active = channels.find(c => c.id === activeId) || null;

    // Load channels + open the SignalR connection once.
    useEffect(() => {
        loadChannels();

        const conn = createChatConnection();
        connRef.current = conn;

        conn.on("NewMessage", (m: Message) => { if (m.channelId === activeIdRef.current) addMessage(m); });
        conn.on("MessageUpdated", (m: Message) => {
            if (m.channelId === activeIdRef.current) setMessages(prev => prev.map(x => x.id === m.id ? m : x));
        });
        conn.on("MessageDeleted", (p: { channelId: string; id: string }) => {
            if (p.channelId === activeIdRef.current) setMessages(prev => prev.filter(x => x.id !== p.id));
        });
        conn.onreconnected(() => { if (activeIdRef.current) conn.invoke("JoinChannel", activeIdRef.current).catch(() => {}); });

        const started = conn.start()
            .then(() => { if (activeIdRef.current) conn.invoke("JoinChannel", activeIdRef.current).catch(() => {}); })
            .catch(() => {});

        return () => {
            // Stop only once negotiation has settled. React 18 StrictMode mounts→unmounts→
            // remounts effects in dev; stopping mid-negotiation throws "connection stopped
            // during negotiation" and can leave real-time flaky.
            started.finally(() => conn.stop().catch(() => {}));
            previewUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the message list pinned to the newest message — except right after prepending a
    // page of older messages, where we restore the previous scroll offset so the user stays
    // on what they were reading instead of being yanked to the bottom.
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        if (prependRef.current != null) {
            el.scrollTop += el.scrollHeight - prependRef.current;
            prependRef.current = null;
            return;
        }
        el.scrollTop = el.scrollHeight;
    }, [messages.length, activeId]);

    // Close the image lightbox on Escape.
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightbox(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox]);

    async function loadChannels() {
        try {
            setError(false);
            const data = await readJson(await api.communityApi.channels());
            const list: Channel[] = data.channels || [];
            setChannels(list);
            setMeId(data.meId || "");
            if (list.length && !activeIdRef.current) openChannel(list[0]);
        } catch (x: any) { setError(true); api.processErrors(x); }
    }

    function addMessage(m: Message) {
        setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
    }

    async function loadMessages(channelId: string, before?: string) {
        setLoadingMsgs(true);
        try {
            const data = await readJson(await api.communityApi.messages(channelId, before ? { before } : {}));
            const batch: Message[] = data.messages || [];
            setHasMore(!!data.hasMore);
            if (before) {
                // Capture the height before the DOM grows so the scroll effect can compensate,
                // and drop any ids already loaded so an overlapping cursor can't double-render.
                prependRef.current = listRef.current?.scrollHeight ?? 0;
                setMessages(prev => {
                    const seen = new Set(prev.map(m => m.id));
                    return [...batch.filter(m => !seen.has(m.id)), ...prev];
                });
            } else {
                setMessages(batch);
            }
        } catch (x: any) { api.processErrors(x); }
        finally { setLoadingMsgs(false); }
    }

    async function openChannel(ch: Channel) {
        const prev = activeIdRef.current;
        if (prev && prev !== ch.id) connRef.current?.invoke("LeaveChannel", prev).catch(() => {});

        setActiveId(ch.id);
        activeIdRef.current = ch.id;
        setMessages([]); setPending([]); setText(""); setEditingId(null); setHasMore(false);
        setMobileChat(true);

        await loadMessages(ch.id);
        connRef.current?.invoke("JoinChannel", ch.id).catch(() => {});
    }

    async function send() {
        if (!active || !active.allowMessages) return;
        const t = text.trim();
        if (!t && pending.length === 0) return;

        try {
            const msg: Message = await readJson(await api.communityApi.sendMessage(active.id, { text: t, attachments: pending }));
            addMessage(msg);
            // Keep the blob previews alive — the message we just added renders from them.
            setText(""); setPending([]);
        } catch (x: any) { api.processErrors(x); }
    }

    async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !active) return;
        setUploading(true);
        try {
            const att: Attachment = await readJson(await api.communityApi.uploadImage(active.id, file));
            const url = URL.createObjectURL(file);
            previewUrlsRef.current.push(url);
            fetchedRef.current.add(att.fileUrl);
            setPreviews(p => ({ ...p, [att.fileUrl]: url }));
            setPending(p => [...p, att]);
        } catch (x: any) { api.processErrors(x); }
        finally { setUploading(false); }
    }

    // A plain <img src> can't load chat images (the public files URL serves the file-browser
    // UI, not the image), so pull each attachment through the auth-gated endpoint as a blob
    // once it shows up in the loaded messages — exactly once per fileUrl.
    useEffect(() => {
        let cancelled = false;
        const urls = Array.from(new Set(messages.flatMap(m => m.attachments.map(a => a.fileUrl))))
            .filter(u => u && !fetchedRef.current.has(u));
        for (const u of urls) {
            fetchedRef.current.add(u);
            api.fetchCommunityImage(u)
                .then(blob => {
                    if (cancelled) return;
                    const obj = URL.createObjectURL(blob);
                    previewUrlsRef.current.push(obj);
                    setPreviews(p => ({ ...p, [u]: obj }));
                })
                .catch(() => { fetchedRef.current.delete(u); }); // allow a later retry
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    async function saveEdit(m: Message) {
        const t = text.trim();
        try {
            const updated: Message = await readJson(await api.communityApi.editMessage(m.id, { text: t }));
            setMessages(prev => prev.map(x => x.id === m.id ? updated : x));
            setEditingId(null); setText("");
        } catch (x: any) { api.processErrors(x); }
    }

    async function del(m: Message) {
        try {
            await api.communityApi.deleteMessage(m.id);
            setMessages(prev => prev.filter(x => x.id !== m.id));
        } catch (x: any) { api.processErrors(x); }
    }

    function removePending(i: number) {
        setPending(p => p.filter((_, j) => j !== i));
    }

    function startEdit(m: Message) { setEditingId(m.id); setText(m.text); }
    function cancelEdit() { setEditingId(null); setText(""); }

    return (
        <div className="community">
            <PageHeader title="Komunita" subtitle="Diskuzní kanály tvého ročníku" />

            <div className={"chat" + (mobileChat ? " showChat" : "")}>
                {/* Channel sidebar */}
                <aside className="channels">
                    <div className="channelsHead">Kanály</div>
                    {channels.length === 0 && !error && <div className="empty small">Zatím tu nejsou žádné kanály.</div>}
                    {error && <div className="empty small">Nepodařilo se načíst. <span className="ghostLink" onClick={loadChannels}>Zkusit znovu</span></div>}
                    <ul>
                        {channels.map(c => (
                            <li key={c.id} className={c.id === activeId ? "active" : ""} onClick={() => openChannel(c)}>
                                <span className="hash">{c.isPublic ? "#" : "🔒"}</span>
                                <span className="cName">{c.name}</span>
                                {c.enableMeeting && <span className="dotMeet" title="Kanál má schůzku" />}
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Messages */}
                <section className="thread">
                    {active ? (
                        <>
                            <header className="threadHead">
                                <span className="back" onClick={() => setMobileChat(false)}>←</span>
                                <div className="ttl">
                                    <h3>{active.isPublic ? "# " : "🔒 "}{active.name}</h3>
                                    {active.description && <p>{active.description}</p>}
                                </div>
                            </header>

                            {active.enableMeeting && active.meetUrl &&
                                <div className="meetBar"><MeetingEmbed url={active.meetUrl} label="Připojit se ke schůzce kanálu" /></div>}

                            <div className="messages" ref={listRef}>
                                {hasMore &&
                                    <div className="loadMore" onClick={() => messages[0] && loadMessages(active.id, messages[0].createdAt)}>
                                        {loadingMsgs ? "Načítám…" : "Načíst starší zprávy"}
                                    </div>}

                                {messages.length === 0 && !loadingMsgs &&
                                    <div className="empty">Zatím žádné zprávy. Napiš první!</div>}

                                {messages.map(m => {
                                    const mine = m.userId === meId;
                                    const admin = !!m.authorIsAdmin;
                                    return (
                                        <div key={m.id} className={"msg" + (mine ? " mine" : "") + (admin ? " fromAdmin" : "")}>
                                            <div className="avatar" aria-hidden>{initials(m.authorName)}</div>
                                            <div className="bubbleWrap">
                                                <div className="meta">
                                                    <span className="author">{mine ? "Ty" : m.authorName}</span>
                                                    <span className="time">{fmtTime(m.createdAt)}{m.editedAt ? " · upraveno" : ""}</span>
                                                </div>
                                                {m.text && <div className="bubble">{m.text}</div>}
                                                {m.attachments.length > 0 &&
                                                    <div className="atts">
                                                        {m.attachments.map((a, i) => {
                                                            const src = previews[a.fileUrl];
                                                            return brokenAtts.has(a.fileUrl) ? (
                                                                <div className="attFallback" key={i} title={a.fileName}>
                                                                    <span className="thumbIcon">🖼</span>
                                                                    <span className="name">{a.fileName}</span>
                                                                </div>
                                                            ) : !src ? (
                                                                <div className="attFallback" key={i} title={a.fileName}>
                                                                    <span className="thumbIcon">🖼</span>
                                                                    <span className="name">Načítání…</span>
                                                                </div>
                                                            ) : (
                                                                <img
                                                                    key={i}
                                                                    src={src}
                                                                    alt={a.fileName}
                                                                    title={a.fileName}
                                                                    onClick={() => setLightbox(src)}
                                                                    onError={() => setBrokenAtts(prev => new Set(prev).add(a.fileUrl))}
                                                                />
                                                            );
                                                        })}
                                                    </div>}
                                                {mine && editingId !== m.id &&
                                                    <div className="rowActions">
                                                        <span onClick={() => startEdit(m)}>Upravit</span>
                                                        <span onClick={() => del(m)}>Smazat</span>
                                                    </div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Composer */}
                            {active.allowMessages ? (
                                <div className="composer">
                                    {pending.length > 0 &&
                                        <div className="pendingAtts">
                                            {pending.map((a, i) => (
                                                <div className="pAtt" key={i}>
                                                    {brokenAtts.has(a.fileUrl) ? (
                                                        <div className="pAttFallback" title={a.fileName}>
                                                            <span className="thumbIcon">🖼</span>
                                                            <span className="name">{a.fileName}</span>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={previews[a.fileUrl]}
                                                            alt={a.fileName}
                                                            onError={() => setBrokenAtts(prev => new Set(prev).add(a.fileUrl))}
                                                        />
                                                    )}
                                                    <span className="rm" onClick={() => removePending(i)}>×</span>
                                                </div>
                                            ))}
                                        </div>}
                                    <div className="composerRow">
                                        {active.allowImages &&
                                            <label className="imgBtn" title="Přidat obrázek">
                                                {uploading ? "…" :
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15l-5-5L5 21" />
                                                        <path d="M3 17V5a2 2 0 0 1 2-2h12" />
                                                        <circle cx="9" cy="9" r="2" />
                                                    </svg>}
                                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickImage} hidden disabled={uploading} />
                                            </label>}
                                        <textarea
                                            value={text}
                                            placeholder={editingId ? "Uprav zprávu…" : "Napiš zprávu…"}
                                            onChange={e => setText(e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    editingId ? saveEdit(messages.find(x => x.id === editingId)!) : send();
                                                }
                                            }}
                                        />
                                        {editingId
                                            ? <>
                                                <button className="sendBtn" onClick={() => saveEdit(messages.find(x => x.id === editingId)!)}>Uložit</button>
                                                <button className="ghostBtn" onClick={cancelEdit}>Zrušit</button>
                                            </>
                                            : <button className="sendBtn" onClick={send} disabled={!text.trim() && pending.length === 0}>Odeslat</button>}
                                    </div>
                                </div>
                            ) : (
                                <div className="readOnly">Tento kanál je jen pro čtení.</div>
                            )}
                        </>
                    ) : (
                        <div className="noChannel">{error ? "Komunitu se nepodařilo načíst." : "Vyber kanál vlevo."}</div>
                    )}
                </section>
            </div>

            {lightbox &&
                <div className="lightbox" onClick={() => setLightbox(null)}>
                    <span className="close" aria-label="Zavřít">×</span>
                    <img src={lightbox} alt="" onClick={e => e.stopPropagation()} />
                </div>}
        </div>
    );
}
